import { useState } from 'react';
import type { ReactNode } from 'react';

import {
  BpkBleed,
  BpkBox,
  BpkBreakpoint,
  BpkFlex,
  BpkSpacing,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';

import CardStack from '../CardStack';
import SelectedChips from '../SelectedChips';
import ThemeCard from '../ThemeCard';
import ThemeCardV2 from '../ThemeCardV2';

import type { CardStackWithThemeCardProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/CardStackWithThemeCardProps';
import type {
  Chip,
  ThemeCardProps,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/ThemeCardProps';
import type { ThemeCardV2Props } from '@skyscanner-internal/landing-pages-shared-types/types-shared/ThemeCardV2Props';

import STYLES from './CardStackWithThemeCard.module.scss';

type CardWithGroupKey = Pick<ThemeCardProps, 'groupKey' | 'trackingData'>;

type CardStackWithThemeCardPropsWithExtraProps = CardStackWithThemeCardProps & {
  callToAction?: ReactNode;
};

const CARD_WIDTH = {
  base: '100%',
  [BpkBreakpoint.SmallTablet]: '50%',
  [BpkBreakpoint.Tablet]: '33.3%',
} as const;

const CARD_MARGIN_BOTTOM = {
  base: BpkSpacing.Base,
  [BpkBreakpoint.SmallTablet]: BpkSpacing.LG,
} as const;

const CARD_STACK_BLEED_END = {
  base: BpkSpacing.None,
  [BpkBreakpoint.SmallTablet]: BpkSpacing.LG,
} as const;

const CARD_PADDING_END = {
  base: BpkSpacing.None,
  [BpkBreakpoint.SmallTablet]: BpkSpacing.LG,
} as const;

const CardStackWithThemeCard = ({
  callToAction = null,
  cards,
  cardsV2 = [],
  disclaimer,
  expandProps = undefined,
  grouping = false,
  imageConfig,
  onCardClick,
  subtitle,
  title,
  useThemeCardV2 = false,
}: CardStackWithThemeCardPropsWithExtraProps) => {
  const activeCards: CardWithGroupKey[] = useThemeCardV2 ? cardsV2 : cards;
  const hasCards = !!activeCards?.length;
  const [activeChipIndex, setActiveChipIndex] = useState(0);
  if (!hasCards) {
    return null;
  }
  const chips = grouping
    ? Array.from(
        new Map(
          activeCards
            .map((item) => item.groupKey)
            .filter((key): key is Chip => {
              if (key !== undefined) {
                return !!key?.name;
              }

              return false;
            })
            .map((key) => {
              const id = key?.name;
              return [id, key]; // 👈 id as the unique key
            }),
        ).values(),
      )
    : [];
  const activeChip = chips[activeChipIndex];
  const isLargeActiveChip = isLargeChip(activeChip);
  const renderCards = chips?.length
    ? activeCards.filter((card) => {
        if (card.groupKey === undefined) {
          return false;
        }
        const isActiveGroup = card.groupKey?.name === activeChip?.name;
        return (
          isActiveGroup &&
          !shouldFilterVanForLargeChip(card, grouping, isLargeActiveChip)
        );
      })
    : activeCards;
  return (
    <BpkVessel className={STYLES.CardStackWithThemeCard}>
      <CardStack
        contentDisplay="block"
        title={title}
        subtitle={subtitle}
        disclaimer={disclaimer}
        callToAction={
          // Hide ExpandableControl if there are less cards than the maxToShowWhenCollapse
          !expandProps || renderCards.length > expandProps.maxToShowWhenCollapse
            ? callToAction
            : null
        }
      >
        <SelectedChips
          chips={chips}
          activeChipIndex={activeChipIndex}
          setActiveChipIndex={setActiveChipIndex}
          cardType="Stack"
        />
        <BpkBleed inlineEnd={CARD_STACK_BLEED_END}>
          <BpkFlex wrap="wrap">
            {(() => {
              let indexInChip = -1;
              return activeCards.map((card, index) => {
                const shouldFilterVan = shouldFilterVanForLargeChip(
                  card,
                  grouping,
                  isLargeActiveChip,
                );
                const isActiveCard =
                  isCurrentChip(grouping, card, activeChip) && !shouldFilterVan;
                indexInChip = isActiveCard ? indexInChip + 1 : indexInChip;
                const isHidden =
                  !isActiveCard ||
                  isHiddenByCollapse(
                    grouping ? indexInChip : index,
                    expandProps,
                  );
                return (
                  <BpkBox
                    key={
                      (card as ThemeCardProps).title ??
                      (card as ThemeCardV2Props).title
                    }
                    data-testid={`themeCardGroup${index}`}
                    display={isHidden ? 'none' : 'flex'}
                    width={CARD_WIDTH}
                    marginBottom={CARD_MARGIN_BOTTOM}
                    paddingEnd={CARD_PADDING_END}
                  >
                    <BpkVessel className={STYLES.card}>
                      {useThemeCardV2 ? (
                        <ThemeCardV2
                          {...(card as ThemeCardV2Props)}
                          index={index}
                        />
                      ) : (
                        <ThemeCard
                          {...(card as ThemeCardProps)}
                          index={index}
                          imageConfig={imageConfig}
                          onClick={
                            onCardClick &&
                            (() => onCardClick(card as ThemeCardProps))
                          }
                        />
                      )}
                    </BpkVessel>
                  </BpkBox>
                );
              });
            })()}
          </BpkFlex>
        </BpkBleed>
      </CardStack>
    </BpkVessel>
  );
};

const isCurrentChip = (
  grouping: boolean,
  card: CardWithGroupKey,
  activeChip: Chip,
) => !grouping || card.groupKey?.name === activeChip?.name;

const shouldFilterVanForLargeChip = (
  card: CardWithGroupKey,
  grouping: boolean,
  isLargeActiveChip: boolean,
) =>
  grouping &&
  isLargeActiveChip &&
  !card?.trackingData?.isDayViewCarTypeData &&
  isVanCard(card);

const isLargeChip = (chip?: Chip) =>
  chip?.name?.toLowerCase().includes('large') ?? false;

const isVanCard = (card: CardWithGroupKey) => {
  // Large is a car class; van / people carrier / SUV are subclasses.
  // As per product (https://skyscanner.slack.com/archives/C0A3FR8EAHK/p1765777367808509), vans won't coexist with Large
  // except for rare fullsize/luxury vans, so filtering vans when Large is active is safe.
  const subClass = card?.trackingData?.subClass?.toLowerCase() ?? '';
  const groupName = card?.groupKey?.name?.toLowerCase() ?? '';
  return groupName.includes('van') || subClass.includes('van');
};

const isHiddenByCollapse = (
  index: number,
  expandProps?: {
    maxToShowWhenCollapse: number;
    isExpand: boolean;
  },
) =>
  expandProps?.isExpand === false && index >= expandProps.maxToShowWhenCollapse;

export default CardStackWithThemeCard;
